import { Workbox } from 'workbox-window';
import { useStore } from 'zustand';
import { createStore } from 'zustand/vanilla';

/** How often a long-running app (a POS left open all day) asks its server for a new version. */
export const UPDATE_CHECK_MS = 60 * 60 * 1000;

/**
 * A new version of the app, downloaded by its service worker and waiting (ARCHITECTURE.md, "Web apps"). It takes over only
 * when the user reloads, so nothing changes under a cashier in the middle of an order.
 */
export interface AppUpdateState {
  waiting: boolean;
  /** Switches to the new version and reloads the page. */
  reload(): void;
}

export const appUpdateStore = createStore<AppUpdateState>(() => ({ waiting: false, reload: () => undefined }));

export function useAppUpdate<T>(selector: (state: AppUpdateState) => T): T {
  return useStore(appUpdateStore, selector);
}

/**
 * Registers the app's service worker (built by `@pos/config/vite` as `sw.js` at the app's base) in production builds.
 * @param base The app's base path, `import.meta.env.BASE_URL`: `/` for the POS and HO admin, `/admin/` for store admin.
 */
export function registerAppServiceWorker({ base, enabled }: { base: string; enabled: boolean }) {
  if (!enabled || typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return undefined;

  const wb = new Workbox(`${base}sw.js`, { scope: base });
  wb.addEventListener('waiting', () =>
    appUpdateStore.setState({
      waiting: true,
      reload: () => {
        wb.addEventListener('controlling', () => window.location.reload());
        wb.messageSkipWaiting();
      },
    }),
  );
  void wb.register().then(() => setInterval(() => void wb.update(), UPDATE_CHECK_MS));
  return wb;
}
